/**
 * Audit Feature Registry
 *
 * Centralized definition of all trackable features across Admin and Core apps.
 * This pattern ensures:
 * 1. Consistent audit event naming
 * 2. DRY configuration
 * 3. Easy discovery of what's tracked
 * 4. Type-safe feature definitions
 */

import {
  AuditAction,
  AuditResource,
  AuditSeverity,
  AuditVisibility,
} from '@hapana/shared/schemas/audit'

export type FeatureCategory =
  | 'authentication'
  | 'clients'
  | 'scheduling'
  | 'payments'
  | 'memberships'
  | 'retail'
  | 'communications'
  | 'settings'
  | 'reporting'
  | 'integrations'
  | 'staff'
  | 'admin'
  | 'support'
  | 'knowledge'

export type AppType = 'admin' | 'core' | 'embed' | 'kiosk' | 'mobile'

export interface FeatureDefinition {
  /** Unique feature identifier (used in analytics) */
  featureId: string

  /** Human-readable feature name */
  displayName: string

  /** Feature category for grouping */
  category: FeatureCategory

  /** Apps where this feature exists */
  apps: AppType[]

  /** Default audit action for this feature's primary operation */
  defaultAction: AuditAction

  /** Resource type this feature operates on */
  resource: AuditResource

  /** Default severity level */
  defaultSeverity: AuditSeverity

  /** Default visibility */
  defaultVisibility: AuditVisibility

  /** Whether this feature involves settings changes */
  isSettings?: boolean

  /** Fields to track for this feature (for settings) */
  trackedFields?: string[]

  /** Whether to send to PostHog analytics */
  sendToPostHog?: boolean

  /** Whether to record in audit log (BigQuery) */
  recordAudit?: boolean

  /** Description of what this feature does */
  description?: string
}

/**
 * Master Feature Registry
 *
 * All trackable features across the platform
 */
export const FEATURE_REGISTRY: Record<string, FeatureDefinition> = {
  // ============================================================================
  // AUTHENTICATION & ACCESS
  // ============================================================================
  'auth.login': {
    featureId: 'auth.login',
    displayName: 'User Login',
    category: 'authentication',
    apps: ['admin', 'core', 'embed', 'kiosk', 'mobile'],
    defaultAction: AuditAction.STAFF_LOGIN,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'User logs into the application',
  },
  'auth.logout': {
    featureId: 'auth.logout',
    displayName: 'User Logout',
    category: 'authentication',
    apps: ['admin', 'core', 'embed', 'kiosk', 'mobile'],
    defaultAction: AuditAction.STAFF_LOGOUT,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'User logs out of the application',
  },
  'auth.login_as': {
    featureId: 'auth.login_as',
    displayName: 'Admin Impersonation (Login As)',
    category: 'authentication',
    apps: ['core'],
    defaultAction: AuditAction.ADMIN_USER_LOGIN,
    resource: AuditResource.ADMIN_USER,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Admin impersonates another user',
  },

  // ============================================================================
  // CLIENT MANAGEMENT (Core App)
  // ============================================================================
  'client.create': {
    featureId: 'client.create',
    displayName: 'Create Client',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_CREATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New client/member created',
  },
  'client.update': {
    featureId: 'client.update',
    displayName: 'Update Client Profile',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    trackedFields: [
      'firstName',
      'lastName',
      'email',
      'phone',
      'dateOfBirth',
      'address',
      'emergencyContact',
      'identificationNumbers',
      'preferredContactMethod',
      'status',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client profile information updated',
  },
  'client.delete': {
    featureId: 'client.delete',
    displayName: 'Delete Client',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_DELETED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client record deleted',
  },
  'client.merge': {
    featureId: 'client.merge',
    displayName: 'Merge Client Accounts',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Multiple client accounts merged into one',
  },
  'client.view_profile': {
    featureId: 'client.view_profile',
    displayName: 'View Client Profile',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_PROFILE_VIEWED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Staff member views client profile',
  },
  'client.note_add': {
    featureId: 'client.note_add',
    displayName: 'Add Client Note',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Note added to client profile',
  },
  'client.document_upload': {
    featureId: 'client.document_upload',
    displayName: 'Upload Client Document',
    category: 'clients',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Document uploaded to client profile',
  },

  // ============================================================================
  // SCHEDULING
  // ============================================================================
  'schedule.session_create': {
    featureId: 'schedule.session_create',
    displayName: 'Create Session/Class',
    category: 'scheduling',
    apps: ['core'],
    defaultAction: AuditAction.SESSION_CREATED,
    resource: AuditResource.SESSION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New session or class created',
  },
  'schedule.session_update': {
    featureId: 'schedule.session_update',
    displayName: 'Update Session/Class',
    category: 'scheduling',
    apps: ['core'],
    defaultAction: AuditAction.SESSION_UPDATED,
    resource: AuditResource.SESSION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    trackedFields: [
      'name',
      'instructor',
      'time',
      'duration',
      'capacity',
      'room',
      'category',
      'description',
      'price',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Session or class details updated',
  },
  'schedule.session_cancel': {
    featureId: 'schedule.session_cancel',
    displayName: 'Cancel Session/Class',
    category: 'scheduling',
    apps: ['core'],
    defaultAction: AuditAction.SESSION_DELETED,
    resource: AuditResource.SESSION,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Session or class cancelled',
  },
  'schedule.booking_create': {
    featureId: 'schedule.booking_create',
    displayName: 'Book Session',
    category: 'scheduling',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.SESSION_BOOKED,
    resource: AuditResource.BOOKING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client books a session',
  },
  'schedule.booking_cancel': {
    featureId: 'schedule.booking_cancel',
    displayName: 'Cancel Booking',
    category: 'scheduling',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.SESSION_CANCELLED,
    resource: AuditResource.BOOKING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Booking cancelled',
  },
  'schedule.waitlist_add': {
    featureId: 'schedule.waitlist_add',
    displayName: 'Add to Waitlist',
    category: 'scheduling',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.SESSION_WAITLISTED,
    resource: AuditResource.BOOKING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client added to session waitlist',
  },
  'schedule.checkin': {
    featureId: 'schedule.checkin',
    displayName: 'Client Check-in',
    category: 'scheduling',
    apps: ['core', 'kiosk'],
    defaultAction: AuditAction.MEMBER_CHECKIN,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client checks in to session',
  },
  'schedule.checkout': {
    featureId: 'schedule.checkout',
    displayName: 'Client Check-out',
    category: 'scheduling',
    apps: ['core', 'kiosk'],
    defaultAction: AuditAction.MEMBER_CHECKOUT,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Client checks out from session',
  },

  // ============================================================================
  // PAYMENTS & BILLING
  // ============================================================================
  'payment.process': {
    featureId: 'payment.process',
    displayName: 'Process Payment',
    category: 'payments',
    apps: ['core', 'embed', 'kiosk', 'mobile'],
    defaultAction: AuditAction.PAYMENT_PROCESSED,
    resource: AuditResource.PAYMENT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Payment successfully processed',
  },
  'payment.refund': {
    featureId: 'payment.refund',
    displayName: 'Issue Refund',
    category: 'payments',
    apps: ['core'],
    defaultAction: AuditAction.PAYMENT_REFUNDED,
    resource: AuditResource.PAYMENT,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Refund issued for payment',
  },
  'payment.failed': {
    featureId: 'payment.failed',
    displayName: 'Payment Failed',
    category: 'payments',
    apps: ['core', 'embed', 'kiosk', 'mobile'],
    defaultAction: AuditAction.PAYMENT_FAILED,
    resource: AuditResource.PAYMENT,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Payment attempt failed',
  },
  'payment.invoice_create': {
    featureId: 'payment.invoice_create',
    displayName: 'Create Invoice',
    category: 'payments',
    apps: ['core'],
    defaultAction: AuditAction.INVOICE_CREATED,
    resource: AuditResource.INVOICE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New invoice created',
  },
  'payment.invoice_void': {
    featureId: 'payment.invoice_void',
    displayName: 'Void Invoice',
    category: 'payments',
    apps: ['core'],
    defaultAction: AuditAction.INVOICE_VOIDED,
    resource: AuditResource.INVOICE,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Invoice voided',
  },

  // ============================================================================
  // MEMBERSHIPS & PACKAGES
  // ============================================================================
  'membership.create': {
    featureId: 'membership.create',
    displayName: 'Create Membership',
    category: 'memberships',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.MEMBERSHIP_CREATED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New membership created',
  },
  'membership.update': {
    featureId: 'membership.update',
    displayName: 'Update Membership',
    category: 'memberships',
    apps: ['core'],
    defaultAction: AuditAction.MEMBERSHIP_UPDATED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Membership details updated',
  },
  'membership.cancel': {
    featureId: 'membership.cancel',
    displayName: 'Cancel Membership',
    category: 'memberships',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.MEMBERSHIP_CANCELLED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Membership cancelled',
  },
  'membership.suspend': {
    featureId: 'membership.suspend',
    displayName: 'Suspend Membership',
    category: 'memberships',
    apps: ['core'],
    defaultAction: AuditAction.MEMBERSHIP_SUSPENDED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Membership suspended/frozen',
  },
  'membership.resume': {
    featureId: 'membership.resume',
    displayName: 'Resume Membership',
    category: 'memberships',
    apps: ['core'],
    defaultAction: AuditAction.MEMBERSHIP_RESUMED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Membership resumed after suspension',
  },
  'membership.renew': {
    featureId: 'membership.renew',
    displayName: 'Renew Membership',
    category: 'memberships',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.MEMBERSHIP_RENEWED,
    resource: AuditResource.MEMBERSHIP,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.CUSTOMER,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Membership renewed',
  },

  // ============================================================================
  // RETAIL & POS
  // ============================================================================
  'retail.sale_create': {
    featureId: 'retail.sale_create',
    displayName: 'Create Retail Sale',
    category: 'retail',
    apps: ['core'],
    defaultAction: AuditAction.PAYMENT_PROCESSED,
    resource: AuditResource.PAYMENT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Retail product sale completed',
  },
  'retail.gift_card_create': {
    featureId: 'retail.gift_card_create',
    displayName: 'Create Gift Card',
    category: 'retail',
    apps: ['core'],
    defaultAction: AuditAction.PAYMENT_PROCESSED,
    resource: AuditResource.PAYMENT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Gift card created',
  },
  'retail.promo_code_create': {
    featureId: 'retail.promo_code_create',
    displayName: 'Create Promo Code',
    category: 'retail',
    apps: ['core'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Promotional code created',
  },

  // ============================================================================
  // COMMUNICATIONS
  // ============================================================================
  'comms.template_create': {
    featureId: 'comms.template_create',
    displayName: 'Create Communication Template',
    category: 'communications',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Email/SMS template created',
  },
  'comms.template_update': {
    featureId: 'comms.template_update',
    displayName: 'Update Communication Template',
    category: 'communications',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Email/SMS template updated',
  },
  'comms.send_message': {
    featureId: 'comms.send_message',
    displayName: 'Send Message to Client',
    category: 'communications',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Message sent to client',
  },
  'comms.bulk_message': {
    featureId: 'comms.bulk_message',
    displayName: 'Send Bulk Message',
    category: 'communications',
    apps: ['core'],
    defaultAction: AuditAction.MEMBER_UPDATED,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Bulk message sent to multiple clients',
  },

  // ============================================================================
  // SETTINGS - LOCATION LEVEL
  // ============================================================================
  'settings.location.general': {
    featureId: 'settings.location.general',
    displayName: 'Location General Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.SITE_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: [
      'name',
      'address',
      'phone',
      'email',
      'timezone',
      'businessHours',
      'currency',
      'language',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'General location settings changed',
  },
  'settings.location.operations': {
    featureId: 'settings.location.operations',
    displayName: 'Location Operations Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.SITE_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: [
      'enableClientsToSignUp',
      'enableWaitlist',
      'autoCheckout',
      'requirePaymentOnBooking',
      'allowWalkIns',
      'capacityLimits',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Location operations settings changed',
  },
  'settings.location.rooms': {
    featureId: 'settings.location.rooms',
    displayName: 'Room/Resource Configuration',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.SITE_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Room or resource settings changed',
  },
  'settings.location.scheduler': {
    featureId: 'settings.location.scheduler',
    displayName: 'Scheduler Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.SITE_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: [
      'defaultSessionDuration',
      'bookingWindow',
      'cancellationPolicy',
      'waitlistEnabled',
      'reminderSettings',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Scheduler settings changed',
  },
  'settings.location.retail': {
    featureId: 'settings.location.retail',
    displayName: 'Retail Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.SITE_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Retail/POS settings changed',
  },

  // ============================================================================
  // SETTINGS - ACCOUNT/BILLING LEVEL
  // ============================================================================
  'settings.account.billing': {
    featureId: 'settings.account.billing',
    displayName: 'Billing Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: ['transactionFees', 'failedPaymentRetry', 'paymentMethods'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Account billing settings changed',
  },
  'settings.account.invoice': {
    featureId: 'settings.account.invoice',
    displayName: 'Invoice Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: ['businessNumber', 'invoiceEmail', 'invoiceTemplate'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Invoice settings changed',
  },
  'settings.account.payroll': {
    featureId: 'settings.account.payroll',
    displayName: 'Payroll Configuration',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: ['payrollTiers', 'staffAssignments', 'compensationRules'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Payroll configuration changed',
  },
  'settings.account.tax': {
    featureId: 'settings.account.tax',
    displayName: 'Tax Settings',
    category: 'settings',
    apps: ['core'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: ['taxRates', 'taxTypes', 'taxExemptions'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Tax settings changed',
  },

  // ============================================================================
  // SETTINGS - BRAND LEVEL (Admin App)
  // ============================================================================
  'settings.brand.general': {
    featureId: 'settings.brand.general',
    displayName: 'Brand General Settings',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: [
      'brandName',
      'brandLogo',
      'brandStatus',
      'primaryColor',
      'secondaryColor',
      'websiteUrl',
    ],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Brand general settings changed',
  },
  'settings.brand.customization': {
    featureId: 'settings.brand.customization',
    displayName: 'Brand Customization',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    trackedFields: ['colorScheme', 'buttonStyles', 'customCSS', 'fonts'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Brand customization changed',
  },
  'settings.brand.access_policies': {
    featureId: 'settings.brand.access_policies',
    displayName: 'Access Policies & Groups',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Access policies or groups changed',
  },
  'settings.brand.fees_rebates': {
    featureId: 'settings.brand.fees_rebates',
    displayName: 'Fees & Rebates Configuration',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Fee or rebate configuration changed',
  },
  'settings.brand.payment_gateway': {
    featureId: 'settings.brand.payment_gateway',
    displayName: 'Payment Gateway Configuration',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.CRITICAL,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Payment gateway settings changed',
  },
  'settings.brand.webhooks': {
    featureId: 'settings.brand.webhooks',
    displayName: 'Webhook Configuration',
    category: 'settings',
    apps: ['admin'],
    defaultAction: AuditAction.BRAND_SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    isSettings: true,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Webhook configuration changed',
  },

  // ============================================================================
  // REPORTING & ANALYTICS
  // ============================================================================
  'reporting.view': {
    featureId: 'reporting.view',
    displayName: 'View Report',
    category: 'reporting',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.REPORT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Report viewed',
  },
  'reporting.export': {
    featureId: 'reporting.export',
    displayName: 'Export Report Data',
    category: 'reporting',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.DATA_EXPORT,
    resource: AuditResource.REPORT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Report data exported',
  },
  'reporting.omni_dashboard': {
    featureId: 'reporting.omni_dashboard',
    displayName: 'View Omni Analytics Dashboard',
    category: 'reporting',
    apps: ['core'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.REPORT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Omni analytics dashboard viewed',
  },

  // ============================================================================
  // INTEGRATIONS
  // ============================================================================
  'integration.connect': {
    featureId: 'integration.connect',
    displayName: 'Connect Integration',
    category: 'integrations',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Third-party integration connected',
  },
  'integration.disconnect': {
    featureId: 'integration.disconnect',
    displayName: 'Disconnect Integration',
    category: 'integrations',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Third-party integration disconnected',
  },
  'integration.configure': {
    featureId: 'integration.configure',
    displayName: 'Configure Integration',
    category: 'integrations',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.SETTING_UPDATED,
    resource: AuditResource.SETTING,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Integration settings configured',
  },

  // ============================================================================
  // STAFF MANAGEMENT
  // ============================================================================
  'staff.create': {
    featureId: 'staff.create',
    displayName: 'Create Staff Member',
    category: 'staff',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.STAFF_CREATED,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New staff member created',
  },
  'staff.update': {
    featureId: 'staff.update',
    displayName: 'Update Staff Member',
    category: 'staff',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.STAFF_UPDATED,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Staff member profile updated',
  },
  'staff.delete': {
    featureId: 'staff.delete',
    displayName: 'Delete Staff Member',
    category: 'staff',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.STAFF_DELETED,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Staff member deleted',
  },
  'staff.role_change': {
    featureId: 'staff.role_change',
    displayName: 'Change Staff Role/Permissions',
    category: 'staff',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.STAFF_ROLE_CHANGED,
    resource: AuditResource.STAFF,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Staff role or permissions changed',
  },

  // ============================================================================
  // ADMIN OPERATIONS (Admin App Only)
  // ============================================================================
  'admin.brand_create': {
    featureId: 'admin.brand_create',
    displayName: 'Create Brand/Corporate',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.CORPORATE_CREATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New brand/corporate created',
  },
  'admin.brand_update': {
    featureId: 'admin.brand_update',
    displayName: 'Update Brand/Corporate',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.CORPORATE_UPDATED,
    resource: AuditResource.CORPORATE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Brand/corporate details updated',
  },
  'admin.location_create': {
    featureId: 'admin.location_create',
    displayName: 'Create Location/Site',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.SITE_CREATED,
    resource: AuditResource.SITE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New location/site created',
  },
  'admin.location_update': {
    featureId: 'admin.location_update',
    displayName: 'Update Location/Site',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.SITE_UPDATED,
    resource: AuditResource.SITE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Location/site details updated',
  },
  'admin.location_delete': {
    featureId: 'admin.location_delete',
    displayName: 'Delete Location/Site',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.SITE_DELETED,
    resource: AuditResource.SITE,
    defaultSeverity: AuditSeverity.CRITICAL,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Location/site deleted',
  },
  'admin.user_create': {
    featureId: 'admin.user_create',
    displayName: 'Create Admin User',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.ADMIN_USER_CREATED,
    resource: AuditResource.ADMIN_USER,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'New admin user created',
  },
  'admin.user_update': {
    featureId: 'admin.user_update',
    displayName: 'Update Admin User',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.ADMIN_USER_UPDATED,
    resource: AuditResource.ADMIN_USER,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Admin user details updated',
  },
  'admin.user_delete': {
    featureId: 'admin.user_delete',
    displayName: 'Delete Admin User',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.ADMIN_USER_DELETED,
    resource: AuditResource.ADMIN_USER,
    defaultSeverity: AuditSeverity.CRITICAL,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Admin user deleted',
  },
  'admin.system_config': {
    featureId: 'admin.system_config',
    displayName: 'System Configuration Change',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.SYSTEM_CONFIG_CHANGED,
    resource: AuditResource.SYSTEM_CONFIG,
    defaultSeverity: AuditSeverity.CRITICAL,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'System configuration changed',
  },
  'admin.ip_blacklist': {
    featureId: 'admin.ip_blacklist',
    displayName: 'IP Blacklist Update',
    category: 'admin',
    apps: ['admin'],
    defaultAction: AuditAction.SYSTEM_CONFIG_CHANGED,
    resource: AuditResource.SYSTEM_CONFIG,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'IP blacklist updated',
  },

  // ============================================================================
  // SUPPORT & AI ASSISTANT
  // ============================================================================
  'support.chat_start': {
    featureId: 'support.chat_start',
    displayName: 'Start Support Chat',
    category: 'support',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.PAGE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Support chat session started',
  },
  'support.chat_message': {
    featureId: 'support.chat_message',
    displayName: 'Support Chat Message',
    category: 'support',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.PAGE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Message sent in support chat',
  },
  'support.ai_response': {
    featureId: 'support.ai_response',
    displayName: 'AI Assistant Response',
    category: 'support',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.PAGE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI assistant provided response',
  },
  'support.escalate': {
    featureId: 'support.escalate',
    displayName: 'Escalate to Human Support',
    category: 'support',
    apps: ['core', 'embed', 'mobile'],
    defaultAction: AuditAction.FEATURE_ACCESSED,
    resource: AuditResource.PAGE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Chat escalated to human support',
  },
  'support.ai_conversation_start': {
    featureId: 'support.ai_conversation_start',
    displayName: 'Start AI Support Conversation',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_CONVERSATION_STARTED,
    resource: AuditResource.AI_CONVERSATION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI support conversation initiated',
  },
  'support.ai_conversation_end': {
    featureId: 'support.ai_conversation_end',
    displayName: 'End AI Support Conversation',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_CONVERSATION_ENDED,
    resource: AuditResource.AI_CONVERSATION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI support conversation ended',
  },
  'support.ai_intent_detected': {
    featureId: 'support.ai_intent_detected',
    displayName: 'AI Intent Detection',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_INTENT_DETECTED,
    resource: AuditResource.AI_INTERACTION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI detected user intent from message',
  },
  'support.ai_response_generated': {
    featureId: 'support.ai_response_generated',
    displayName: 'AI Response Generated',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_RESPONSE_GENERATED,
    resource: AuditResource.AI_INTERACTION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI generated response for support chat',
  },
  'support.ai_feedback': {
    featureId: 'support.ai_feedback',
    displayName: 'AI Response Feedback',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_FEEDBACK_PROVIDED,
    resource: AuditResource.AI_INTERACTION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'User provided feedback on AI response',
  },
  'support.ai_ticket_created': {
    featureId: 'support.ai_ticket_created',
    displayName: 'AI Support Ticket Created',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_SUPPORT_TICKET_CREATED,
    resource: AuditResource.SUPPORT_TICKET,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Support ticket created from AI conversation',
  },
  'support.ai_knowledge_search': {
    featureId: 'support.ai_knowledge_search',
    displayName: 'AI Knowledge Base Search',
    category: 'support',
    apps: ['admin'],
    defaultAction: AuditAction.AI_KNOWLEDGE_SEARCH,
    resource: AuditResource.AI_INTERACTION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'AI searched knowledge base for answers',
  },

  // ============================================================================
  // KNOWLEDGE HUB
  // ============================================================================
  'knowledge.article_create': {
    featureId: 'knowledge.article_create',
    displayName: 'Create Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_CREATED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article created',
  },
  'knowledge.article_update': {
    featureId: 'knowledge.article_update',
    displayName: 'Update Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_UPDATED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    trackedFields: ['title', 'content', 'category', 'status', 'coverImage'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article updated',
  },
  'knowledge.article_publish': {
    featureId: 'knowledge.article_publish',
    displayName: 'Publish Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_PUBLISHED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article published',
  },
  'knowledge.article_archive': {
    featureId: 'knowledge.article_archive',
    displayName: 'Archive Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_ARCHIVED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article archived',
  },
  'knowledge.article_delete': {
    featureId: 'knowledge.article_delete',
    displayName: 'Delete Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_DELETED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article deleted',
  },
  'knowledge.article_restore': {
    featureId: 'knowledge.article_restore',
    displayName: 'Restore Knowledge Article',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_RESTORED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base article restored from deletion',
  },
  'knowledge.article_translation_add': {
    featureId: 'knowledge.article_translation_add',
    displayName: 'Add Article Translation',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_TRANSLATION_ADDED,
    resource: AuditResource.KNOWLEDGE_TRANSLATION,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Translation added to knowledge article',
  },
  'knowledge.category_create': {
    featureId: 'knowledge.category_create',
    displayName: 'Create Knowledge Category',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_CATEGORY_CREATED,
    resource: AuditResource.KNOWLEDGE_CATEGORY,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base category created',
  },
  'knowledge.category_update': {
    featureId: 'knowledge.category_update',
    displayName: 'Update Knowledge Category',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_CATEGORY_UPDATED,
    resource: AuditResource.KNOWLEDGE_CATEGORY,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    trackedFields: ['name', 'slug', 'color', 'icon'],
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base category updated',
  },
  'knowledge.category_delete': {
    featureId: 'knowledge.category_delete',
    displayName: 'Delete Knowledge Category',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_CATEGORY_DELETED,
    resource: AuditResource.KNOWLEDGE_CATEGORY,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base category deleted',
  },
  'knowledge.category_reorder': {
    featureId: 'knowledge.category_reorder',
    displayName: 'Reorder Knowledge Categories',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_CATEGORY_REORDERED,
    resource: AuditResource.KNOWLEDGE_CATEGORY,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge base categories reordered',
  },
  'knowledge.ai_article_generate': {
    featureId: 'knowledge.ai_article_generate',
    displayName: 'Generate Article with AI',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.AI_ARTICLE_GENERATED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Knowledge article generated using AI',
  },
  'knowledge.article_video_upload': {
    featureId: 'knowledge.article_video_upload',
    displayName: 'Upload Article Video',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_VIDEO_UPLOADED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Video uploaded to knowledge article',
  },
  'knowledge.article_image_upload': {
    featureId: 'knowledge.article_image_upload',
    displayName: 'Upload Article Image',
    category: 'knowledge',
    apps: ['admin'],
    defaultAction: AuditAction.KNOWLEDGE_ARTICLE_IMAGE_UPLOADED,
    resource: AuditResource.KNOWLEDGE_ARTICLE,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Image uploaded to knowledge article',
  },

  // ============================================================================
  // DATA & COMPLIANCE
  // ============================================================================
  'data.export_request': {
    featureId: 'data.export_request',
    displayName: 'Data Export Request',
    category: 'admin',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.DATA_EXPORT,
    resource: AuditResource.REPORT,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Data export requested',
  },
  'data.gdpr_request': {
    featureId: 'data.gdpr_request',
    displayName: 'GDPR Data Request',
    category: 'admin',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.GDPR_REQUEST,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.SECURITY,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'GDPR data access request',
  },
  'data.deletion_request': {
    featureId: 'data.deletion_request',
    displayName: 'Data Deletion Request',
    category: 'admin',
    apps: ['core', 'admin'],
    defaultAction: AuditAction.DATA_DELETION,
    resource: AuditResource.MEMBER,
    defaultSeverity: AuditSeverity.CRITICAL,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Data deletion request',
  },

  // ============================================================================
  // DOOR ACCESS & SECURITY
  // ============================================================================
  'door.access_granted': {
    featureId: 'door.access_granted',
    displayName: 'Door Access Granted',
    category: 'authentication',
    apps: ['kiosk'],
    defaultAction: AuditAction.DOOR_ACCESS_GRANTED,
    resource: AuditResource.DOOR,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Door access granted to member',
  },
  'door.access_denied': {
    featureId: 'door.access_denied',
    displayName: 'Door Access Denied',
    category: 'authentication',
    apps: ['kiosk'],
    defaultAction: AuditAction.DOOR_ACCESS_DENIED,
    resource: AuditResource.DOOR,
    defaultSeverity: AuditSeverity.WARNING,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Door access denied to member',
  },
  'door.unlock': {
    featureId: 'door.unlock',
    displayName: 'Door Unlocked',
    category: 'authentication',
    apps: ['core', 'kiosk'],
    defaultAction: AuditAction.DOOR_UNLOCKED,
    resource: AuditResource.DOOR,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Door manually unlocked',
  },
  'door.lock': {
    featureId: 'door.lock',
    displayName: 'Door Locked',
    category: 'authentication',
    apps: ['core', 'kiosk'],
    defaultAction: AuditAction.DOOR_LOCKED,
    resource: AuditResource.DOOR,
    defaultSeverity: AuditSeverity.INFO,
    defaultVisibility: AuditVisibility.INTERNAL,
    sendToPostHog: true,
    recordAudit: true,
    description: 'Door manually locked',
  },
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a feature definition by ID
 */
export function getFeature(featureId: string): FeatureDefinition | undefined {
  return FEATURE_REGISTRY[featureId]
}

/**
 * Get all features for a specific app
 */
export function getFeaturesByApp(app: AppType): FeatureDefinition[] {
  return Object.values(FEATURE_REGISTRY).filter((feature) => feature.apps.includes(app))
}

/**
 * Get all features in a specific category
 */
export function getFeaturesByCategory(category: FeatureCategory): FeatureDefinition[] {
  return Object.values(FEATURE_REGISTRY).filter((feature) => feature.category === category)
}

/**
 * Get all features that record to the audit log
 */
export function getAuditableFeatures(): FeatureDefinition[] {
  return Object.values(FEATURE_REGISTRY).filter((feature) => feature.recordAudit !== false)
}

/**
 * Get all features that send to PostHog
 */
export function getPostHogFeatures(): FeatureDefinition[] {
  return Object.values(FEATURE_REGISTRY).filter((feature) => feature.sendToPostHog !== false)
}

/**
 * Get all setting-related features
 */
export function getSettingsFeatures(): FeatureDefinition[] {
  return Object.values(FEATURE_REGISTRY).filter((feature) => feature.isSettings === true)
}

/**
 * Get feature counts by category
 */
export function getFeatureCountsByCategory(): Record<FeatureCategory, number> {
  const counts = {} as Record<FeatureCategory, number>
  for (const feature of Object.values(FEATURE_REGISTRY)) {
    counts[feature.category] = (counts[feature.category] || 0) + 1
  }
  return counts
}

/**
 * Get feature counts by app
 */
export function getFeatureCountsByApp(): Record<AppType, number> {
  const counts = {} as Record<AppType, number>
  for (const feature of Object.values(FEATURE_REGISTRY)) {
    for (const app of feature.apps) {
      counts[app] = (counts[app] || 0) + 1
    }
  }
  return counts
}

/**
 * Validate that a feature ID exists
 */
export function isValidFeatureId(featureId: string): boolean {
  return featureId in FEATURE_REGISTRY
}

/**
 * Get all feature IDs as an array
 */
export function getAllFeatureIds(): string[] {
  return Object.keys(FEATURE_REGISTRY)
}

/**
 * Get all unique categories
 */
export function getAllCategories(): FeatureCategory[] {
  return [...new Set(Object.values(FEATURE_REGISTRY).map((f) => f.category))]
}

/**
 * Export feature registry summary for documentation
 */
export function getRegistrySummary(): {
  totalFeatures: number
  byCategory: Record<FeatureCategory, number>
  byApp: Record<AppType, number>
  auditableCount: number
  postHogCount: number
  settingsCount: number
} {
  return {
    totalFeatures: Object.keys(FEATURE_REGISTRY).length,
    byCategory: getFeatureCountsByCategory(),
    byApp: getFeatureCountsByApp(),
    auditableCount: getAuditableFeatures().length,
    postHogCount: getPostHogFeatures().length,
    settingsCount: getSettingsFeatures().length,
  }
}
